<script setup>
import { ref, computed, watch, onMounted, nextTick, useSlots } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { Tooltip as VTooltip } from 'floating-vue';

const props = defineProps({
  conversationLabels: {
    type: Array,
    required: true,
  },
  // BonBoZ: chip nền màu đặc kiểu Pancake; là prop vì SLAReportItem.vue vẫn
  // phải giữ kiểu `smooth` của upstream.
  solid: {
    type: Boolean,
    default: false,
  },
});

const slots = useSlots();
const accountLabels = useMapGetter('labels/getLabels');

const activeLabels = computed(() => {
  return accountLabels.value.filter(({ title }) =>
    props.conversationLabels.includes(title)
  );
});

const showAllLabels = ref(false);
const showExpandLabelButton = ref(false);
const labelPosition = ref(-1);
const labelContainer = ref(null);

// BonBoZ: chỗ dành sẵn cho chip "+N" để nó không bị hàng thẻ cắt mất.
// 40px vì chip viên thuốc đệm rộng hơn (px-2 ở 2xl) so với bản bo góc cũ.
const OVERFLOW_CHIP_WIDTH = 40;

const hiddenLabels = computed(() =>
  props.solid ? activeLabels.value.slice(labelPosition.value + 1) : []
);

const computeVisibleLabelPosition = () => {
  const beforeSlot = slots.before ? 100 : 0;
  if (!labelContainer.value) {
    return;
  }

  const labels = Array.from(labelContainer.value.querySelectorAll('.label'));
  const available = labelContainer.value.clientWidth - beforeSlot;
  let labelOffset = 0;
  showExpandLabelButton.value = false;
  labelPosition.value = -1;
  labels.forEach((label, index) => {
    labelOffset += label.offsetWidth + 8;
    // Thẻ cuối không cần chừa chỗ vì khi nó vừa thì chẳng còn gì để ẩn.
    const isLast = index === labels.length - 1;
    if (labelOffset <= available - (isLast ? 0 : OVERFLOW_CHIP_WIDTH)) {
      labelPosition.value = index;
    } else {
      showExpandLabelButton.value = labels.length > 1;
    }
  });

  // BonBoZ: luôn giữ ít nhất một chip — tên dài thì để ellipsis cắt.
  if (labelPosition.value === -1 && labels.length) {
    labelPosition.value = 0;
  }
};

watch([activeLabels, () => props.solid], () => {
  nextTick(() => computeVisibleLabelPosition());
});

onMounted(() => computeVisibleLabelPosition());

const onShowLabels = e => {
  e.stopPropagation();
  showAllLabels.value = !showAllLabels.value;
  nextTick(() => computeVisibleLabelPosition());
};
</script>

<template>
  <div ref="labelContainer" v-resize="computeVisibleLabelPosition">
    <div
      v-if="activeLabels.length || $slots.before"
      class="flex items-center flex-shrink min-w-0 gap-y-1"
      :class="{ 'h-auto overflow-visible flex-row flex-wrap': showAllLabels }"
    >
      <slot name="before" />
      <woot-label
        v-for="(label, index) in activeLabels"
        :key="label ? label.id : index"
        :title="label.title"
        :description="label.description"
        :color="solid ? '' : label.color"
        :bg-color="solid ? label.color : ''"
        :variant="solid ? '' : 'smooth'"
        class="!mb-0"
        small
        :class="[
          // BonBoZ: chip cao đúng bằng hàng thẻ đã khoá, nếu không viền bị
          // `overflow-hidden` xén.
          // `!rounded-full` vì `@apply rounded-[4px]` scoped trong ui/Label.vue
          // thắng utility thường; đè ở đây để không bo tròn chip nơi khác.
          solid
            ? 'max-w-[calc(100%-2.5rem)] !h-[18px] 2xl:!h-5 3xl:!h-[22px] !text-[10px] 2xl:!text-[11px] 3xl:!text-xs !px-1.5 2xl:!px-2 !rounded-full'
            : 'max-w-[calc(100%-0.5rem)]',
          // Chỉ chip cuối cùng còn hiện được mới co, để nó ellipsis thay vì cả
          // hàng cùng bẹp thành ô màu rỗng chữ.
          {
            'min-w-0': solid && index === labelPosition,
            'flex-shrink-0': solid && index !== labelPosition,
            'invisible absolute': !showAllLabels && index > labelPosition,
          },
        ]"
      />
      <!-- BonBoZ: hover đúng chip "+N" để xem các thẻ bị hàng thẻ cắt. -->
      <VTooltip
        v-if="hiddenLabels.length"
        placement="top"
        popper-class="bonboz-bang-the"
        class="flex-shrink-0"
        @click.stop
      >
        <span
          class="inline-flex items-center h-[18px] 2xl:h-5 3xl:h-[22px] px-1.5 2xl:px-2 text-[10px] 2xl:text-[11px] 3xl:text-xs font-medium rounded-full text-n-slate-12 bg-n-slate-3 border border-solid border-[rgb(var(--pk-vien))]"
        >
          {{ `+${hiddenLabels.length}` }}
        </span>
        <template #popper>
          <div class="flex flex-wrap gap-1 max-w-56">
            <woot-label
              v-for="label in hiddenLabels"
              :key="`hidden-${label.id}`"
              :title="label.title"
              :bg-color="label.color"
              class="!mb-0 !mr-0 !px-2 !rounded-full"
              small
            />
          </div>
        </template>
      </VTooltip>
      <button
        v-if="showExpandLabelButton && !solid"
        :title="
          showAllLabels
            ? $t('CONVERSATION.CARD.HIDE_LABELS')
            : $t('CONVERSATION.CARD.SHOW_LABELS')
        "
        class="h-5 py-0 px-1 flex-shrink-0 mr-6 ml-0 rtl:ml-6 rtl:mr-0 rtl:rotate-180 text-n-slate-11 border-n-strong dark:border-n-strong"
        @click="onShowLabels"
      >
        <fluent-icon
          :icon="showAllLabels ? 'chevron-left' : 'chevron-right'"
          size="12"
        />
      </button>
    </div>
  </div>
</template>
